import { CreditSubscription } from "@custom-types/credit-types";
import { useCreditSettings } from "@hooks/subscription/use-credit-settings";
import { useSubscriptionSettings } from "@hooks/subscription/use-subscription-settings";
import { useUsageMetrics } from "@hooks/subscription/use-usage-metrics";
import { Grid2, Skeleton } from "@mui/material";
import { BaseSettingsPage } from "@pages/settings/base-settings-page";
import { NoCredits } from "@pages/settings/subscription-settings/credits/no-credits";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import {
  allCreditSubscriptionsSelector,
  isFeatureAvailableInCompanySelector,
  isFetchingSdbCompanySelector,
} from "@store/sdb-company/sdb-company-selector";
import { fetchCompanySubscriptions } from "@store/sdb-company/sdb-company-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { useEffect } from "react";

/**
 * Shows the subscription details
 */
export function SubscriptionSettings(): JSX.Element {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(isFetchingSdbCompanySelector);

  const { subscriptionSectionProps } = useSubscriptionSettings();
  const { usageSectionProps } = useUsageMetrics();
  const { creditsSectionProps } = useCreditSettings();

  const creditSubscriptions: CreditSubscription[] = useAppSelector(
    allCreditSubscriptionsSelector
  );

  const isFloorplanGeneratorFeatureEnabled = useAppSelector(
    isFeatureAvailableInCompanySelector("floorplan-generator")
  );

  // Whether the "No tokens" section should show
  // Only show to users with the floorplan feature who never had tokens
  const shouldShowNoTokensSection =
    isFloorplanGeneratorFeatureEnabled && creditSubscriptions.length === 0;

  // Fetch the company subscriptions when the component mounts
  // Required to display the updated balance after token purchase
  useEffect(() => {
    function fetchSubscription(): void {
      /**
       * Fetch company subscriptions with status active, expired and invalid. subscriptions can have the following statuses:
       * Active - between start and end date
       * Expired - end date is in the past
       * Invalid - between start and end date but at least one constraint is over the limit and
       * thus the subscription is not providing the Features it contains.
       */
      const queryOptions: FaroCoreApiTypes.CompanySubscriptionOptions = {
        status: ["ACTIVE", "EXPIRED", "INVALID"],
      };

      void dispatch(
        fetchCompanySubscriptions({
          queryOptions,
        })
      );
    }

    fetchSubscription();
  }, [dispatch]);

  return (
    <>
      <BaseSettingsPage
        pageSectionProps={[
          ...subscriptionSectionProps,
          ...usageSectionProps,
          ...creditsSectionProps,
        ]}
        isLoading={isLoading}
        dataTestId="workspace-settings-subscription"
        customSx={{ mt: "45px" }}
      />
      {(isLoading || shouldShowNoTokensSection) && (
        <Grid2 mt="45px">
          {isLoading ? (
            <Skeleton
              variant="rectangular"
              sx={{ width: "100%", height: "350px" }}
            />
          ) : (
            shouldShowNoTokensSection && <NoCredits />
          )}
        </Grid2>
      )}
    </>
  );
}
